import { FIREBASE_CONFIG } from "config";
import { logInfo } from "services/logging";
import { api } from "services/restUtil";
import { memoizeAsyncFactory } from "utils/async";

const SESSION_INVITE_TYPE = "session_invite";
const OTHER_CONNECTED_TYPE = "other_user_connected";
const NOTIFICATION_TITLE = "Talaera Session";
const NOTIFICATION_ICON = "/favicon.png";

type SaveFirebaseNotificationTokenPayload = {
  token: string;
};

type ForegroundMessagePayload = {
  data: {
    type: string;
    data: string;
  };
  notification?: {
    click_action?: string;
  };
};

type SessionNotificationData = {
  other_user?: {
    first_name?: string | null;
  } | null;
  lesson?: {
    scheduled?: string | null;
  } | null;
};

export async function registerPushNotificationToken() {
  const token = await getToken();
  return api.post<SaveFirebaseNotificationTokenPayload>("/push/token", { token });
}

async function getToken(): Promise<string> {
  const messaging = await getFirebaseMessaging();
  try {
    let currentToken = await messaging.getToken();
    if (!currentToken) {
      await Notification.requestPermission();
      currentToken = await messaging.getToken();
    }
    return currentToken;
  } catch (error) {
    logInfo("got error while requesting firebase notifications token", {
      from: "getToken",
      error,
    });
    throw new Error("Unable to get messaging token.");
  }
}

function onMessage(messagePayload: ForegroundMessagePayload) {
  logInfo("Message received. ", messagePayload);

  const body = getNotificationBody(messagePayload);
  if (
    body === null ||
    !("Notification" in window) ||
    Notification.permission !== "granted"
  ) {
    return;
  }

  const notification = new Notification(NOTIFICATION_TITLE, {
    body,
    icon: NOTIFICATION_ICON,
  });

  notification.onclick = event => {
    event.preventDefault();

    const clickAction = messagePayload.notification?.click_action;
    if (clickAction) {
      window.open(clickAction, "_blank");
    }

    notification.close();
  };
}

function getNotificationBody(payload: ForegroundMessagePayload) {
  const notificationType = payload.data.type;
  if (
    notificationType !== SESSION_INVITE_TYPE &&
    notificationType !== OTHER_CONNECTED_TYPE
  ) {
    return null;
  }

  const data = JSON.parse(payload.data.data) as SessionNotificationData;
  const otherUserName = data.other_user?.first_name;

  if (notificationType === SESSION_INVITE_TYPE) {
    let body = otherUserName
      ? `Your Talaera lesson with ${otherUserName}`
      : "Your Talaera lesson";

    if (
      data.lesson?.scheduled &&
      Date.now() >= new Date(data.lesson.scheduled).getTime()
    ) {
      body += " has started.";
    } else {
      body += " starts in five minutes.";
    }

    return body;
  }

  return `${otherUserName || "The other user"} has just joined your Talaera session.`;
}

async function initializeFirebaseMessaging() {
  const firebaseModule = await import("firebase/compat/app");
  await import("firebase/compat/messaging");

  const firebase = firebaseModule.default;
  if (!firebase.apps.length) {
    firebase.initializeApp(FIREBASE_CONFIG);
  }

  if (typeof firebase.messaging !== "function") {
    throw new Error("firebase.messaging is not available");
  }

  const messaging = firebase.messaging();
  if (!messaging) {
    throw new Error("firebase.messaging() is not found");
  }

  messaging.onMessage(onMessage);

  return messaging;
}

const getFirebaseMessaging = memoizeAsyncFactory(initializeFirebaseMessaging);
